import { useCallback, useEffect } from 'react';

import { useFreshRef } from './useFreshRef';

interface EventListenerOptions {
  eventName: string;
  callback: (event: Event) => void;
  options?: {
    disabled?: boolean;
    capture?: boolean;
  };
  target?: EventTarget;
}

/**
 * Hook that adds an event listener to a target element
 * @param params Object containing eventName, callback, options, and target
 */
export const useEventListener = ({
  eventName,
  callback,
  options,
  target = document,
}: EventListenerOptions): void => {
  const callbackRef = useFreshRef<(event: Event) => void>(callback);

  const enabled = !options?.disabled;
  const capture = options?.capture;

  const handleEvent = useCallback(
    (event: Event): void => {
      if (callbackRef.current) {
        callbackRef.current(event);
      }
    },
    [callbackRef]
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    target.addEventListener(eventName, handleEvent, capture);

    return () => target.removeEventListener(eventName, handleEvent, capture);
  }, [enabled, eventName, handleEvent, capture, target]);
};
